<template>
  <div class="order-attachment" v-if="enabled">
    <div class="section-title p-0">
      <h5>{{ $t("order_attachment.title") }}</h5>
    </div>
    <p class="mb-1">{{ required ? $t("order_attachment.required_hint") : $t("order_attachment.optional_hint") }}</p>
    <p class="mb-3">
      <small>
        {{ $t("order_attachment.formats_hint", { formats: formatsLabel }) }}
        <template v-if="maxSizeMb > 0">{{ $t("order_attachment.size_hint", { size: maxSizeMb }) }}</template>
      </small>
    </p>

    <ul class="order-attachment-list" v-if="files.length">
      <li v-for="(file, index) in files" :key="file.id">
        <a href="javascript:void(0)" @click="download(file, index)">{{ itemLabel(file, index) }}</a>
        <a href="javascript:void(0)" class="ml-3" :class="{ disabled: removingId !== null }" @click="remove(file.id)">{{ $t("buttons.delete") }}</a>
      </li>
    </ul>

    <a class="btn-yellow pointer" :class="{ disabled: uploading }" href="javascript:void(0)" @click="openPicker()">
      {{ uploading ? $t("order_attachment.uploading") : $t("order_attachment.upload") }}
    </a>
    <input type="file" ref="fileInput" class="d-none" :accept="accept" @change="upload($event)">
  </div>
</template>

<script>
import axiosInstance from '../../main';
import orderAttachmentMixin, { MIME_EXTENSIONS } from '@/mixins/orderAttachmentMixin';

export default {
  name: 'OrderAttachment',
  mixins: [orderAttachmentMixin],
  props: {
    config: { type: Object, default: () => ({}) },
    files: { type: Array, default: () => [] }
  },
  emits: ['changed', 'error', 'uploading'],
  data() {
    return {
      uploading: false,
      removingId: null
    }
  },
  computed: {
    enabled() {
      return ['required', 'optional'].includes(this.config?.mode);
    },
    required() {
      return this.config?.mode === 'required';
    },
    maxSize() {
      const size = Number(this.config?.max_size);
      return Number.isFinite(size) && size > 0 ? size : null;
    },
    maxSizeMb() {
      return this.maxSize ? Math.round(this.maxSize / (1024 * 1024)) : 0;
    },
    // The contract is MIME types (image/png); extensions (png) are accepted too so both shapes work
    allowedMimes() {
      return (this.config?.allowed_types ?? []).filter(type => type.includes('/'));
    },
    allowedExtensions() {
      const fromMimes = this.allowedMimes.flatMap(type => MIME_EXTENSIONS[type] ?? []);
      const declared = (this.config?.allowed_types ?? []).filter(type => !type.includes('/')).map(type => type.toLowerCase());
      return [...new Set([...fromMimes, ...declared])];
    },
    accept() {
      return [...this.allowedExtensions.map(ext => '.' + ext), ...this.allowedMimes].join(',');
    },
    formatsLabel() {
      return [...new Set(this.allowedExtensions.map(ext => (ext === 'jpeg' ? 'jpg' : ext).toUpperCase()))].join(', ');
    }
  },
  watch: {
    uploading(value) {
      this.$emit('uploading', value);
    }
  },
  methods: {
    itemLabel(file, index) {
      const extension = (MIME_EXTENSIONS[file.type ?? file.tipo] ?? [''])[0].toUpperCase();
      return `${this.$t('order_attachment.item', { n: index + 1 })}${extension ? ' (' + extension + ')' : ''}`;
    },
    openPicker() {
      if (!this.uploading) {
        this.$refs.fileInput.click();
      }
    },
    // Client checks only give early feedback; the backend validates the real content
    validate(file) {
      const extension = (file.name.split('.').pop() || '').toLowerCase();
      const typeAllowed = file.type
          ? this.allowedMimes.includes(file.type) || this.allowedExtensions.includes(extension)
          : this.allowedExtensions.includes(extension);
      if (this.allowedExtensions.length && !typeAllowed) {
        return 'ORDER_ATTACHMENT_INVALID_TYPE';
      }
      if (this.maxSize && file.size > this.maxSize) {
        return 'ORDER_ATTACHMENT_TOO_LARGE';
      }
      return null;
    },
    upload(event) {
      const file = event.target.files[0];
      event.target.value = '';
      if (!file || this.uploading) return;

      const error = this.validate(file);
      if (error) {
        this.$emit('error', this.attachmentFriendlyError(error));
        return;
      }

      const formData = new FormData();
      formData.append('files[]', file);
      this.uploading = true;
      axiosInstance.post(this.$woe_api_uri + '/cart/upload', formData)
          .then(() => this.$emit('changed'))
          .catch((e) => this.$emit('error', this.attachmentFriendlyError(e.response?.data?.error?.message)))
          .finally(() => {
            this.uploading = false;
          });
    },
    async remove(id) {
      if (this.removingId !== null) return;

      const confirmed = await this.$vbsModal.confirm({
        title: this.$t('order_attachment.confirm_delete_title'),
        message: this.$t('order_attachment.confirm_delete_text'),
        leftBtnText: this.$t('buttons.cancel'),
        rightBtnText: this.$t('buttons.accept'),
        displayCloseBtn: false
      });
      if (!confirmed) return;

      this.removingId = id;
      axiosInstance.post(this.$woe_api_uri + '/cart/upload/remove', { id })
          .then(() => this.$emit('changed'))
          .catch((e) => this.$emit('error', this.attachmentFriendlyError(e.response?.data?.error?.message)))
          .finally(() => {
            this.removingId = null;
          });
    },
    download(file, index) {
      this.downloadOrderAttachment(file.id, this.$t('order_attachment.item', { n: index + 1 }))
          .catch((e) => this.$emit('error', e.message));
    }
  }
}
</script>

<style scoped>
.order-attachment-list {
  list-style: none;
  padding: 0;
}

/* same spacing as the order summary block (.yellow-bg) right above it */
.order-attachment {
  padding: 20px 35px;
}

/* the title lines up with the text of the block instead of keeping the section title indent */
.order-attachment .section-title h5 {
  padding: 0 0 8px;
}

/* the global .btn-yellow is centered with margin auto; here it aligns with the rest of the block */
.order-attachment .btn-yellow {
  display: inline-block;
  margin: 0;
  float: none;
}

.btn-yellow.disabled,
.order-attachment-list a.disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
